import { useCallback, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { OutputDeliverableMetricsResponse } from '@/types/composer-dashboard';
import { Skeleton } from '@/components/ui/skeleton';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Rectangle,
} from 'recharts';
import type { RectangleProps } from 'recharts';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
} from '@/components/ui/chart';
import { SectionHeading } from './section-heading';
import { getChartPaletteColor } from './helpers/model-colors';

const CHART_VARS: Record<string, string> = {
  Word: 'var(--chart-1)',
  PowerPoint: 'var(--chart-2)',
  Excel: 'var(--chart-3)',
  PDF: 'var(--chart-4)',
  'HTML / Viz': 'var(--chart-5)',
  Video: 'var(--chart-6)',
  Image: 'var(--chart-7)',
  Other: 'var(--chart-8)',
};

const BAR_SIZE = 28;

type OtherSegment = {
  key: string;
  label: string;
  mime: string;
  count: number;
  fill: string;
};

type ChartRow = {
  name: string;
  count: number;
  percentage: number;
  fill: string;
  _segments?: OtherSegment[];
};

type HoveredSegment = {
  segment: OtherSegment;
  x: number;
  y: number;
};

function formatMimeLabel(mime: string): string {
  const ext = mime.split('/').pop();
  return ext && ext !== mime ? ext : mime;
}

function createOutputBarShape(
  onSegmentHover: (
    segment: OtherSegment | null,
    event?: MouseEvent<SVGElement>,
  ) => void,
) {
  return function OutputBarShape(
    props: RectangleProps & { payload?: ChartRow },
  ) {
    const { x = 0, y = 0, width = 0, height = 0, payload } = props;
    if (!payload || width <= 0) return null;

    const segments = payload._segments;
    if (segments?.length) {
      const total = segments.reduce((sum, seg) => sum + seg.count, 0);
      if (total <= 0) return null;

      let offsetX = x;
      return (
        <g>
          {segments.map((seg, index) => {
            const segWidth = (seg.count / total) * width;
            const isFirst = index === 0;
            const isLast = index === segments.length - 1;
            const rect = (
              <Rectangle
                key={seg.key}
                x={offsetX}
                y={y}
                width={Math.max(segWidth, 0)}
                height={height}
                fill={seg.fill}
                radius={
                  isFirst && isLast
                    ? [0, 4, 4, 0]
                    : isLast
                      ? [0, 4, 4, 0]
                      : [0, 0, 0, 0]
                }
                className='cursor-pointer'
                onMouseEnter={(event) => onSegmentHover(seg, event)}
                onMouseLeave={() => onSegmentHover(null)}
              />
            );
            offsetX += segWidth;
            return rect;
          })}
        </g>
      );
    }

    return (
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill={payload.fill}
        radius={[0, 4, 4, 0]}
      />
    );
  };
}

function OutputTypeTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{
    dataKey?: string | number;
    payload?: ChartRow;
  }>;
}) {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload;
  if (!row || row._segments?.length) return null;

  return (
    <div className='rounded-lg border bg-background px-3 py-2 text-xs shadow-md'>
      <p className='font-medium'>{row.name}</p>
      <p className='text-muted-foreground'>
        {row.count} workflow{row.count === 1 ? '' : 's'} ({row.percentage}%)
      </p>
    </div>
  );
}

interface OutputDeliverableSectionProps {
  metrics: OutputDeliverableMetricsResponse | undefined;
  isLoading?: boolean;
  error?: Error | null;
}

export function OutputDeliverableSection({
  metrics,
  isLoading = false,
  error,
}: OutputDeliverableSectionProps) {
  const [hoveredSegment, setHoveredSegment] = useState<HoveredSegment | null>(
    null,
  );

  const otherBreakdown = useMemo(
    () => metrics?.other_mime_breakdown ?? [],
    [metrics?.other_mime_breakdown],
  );

  const chartData = useMemo((): ChartRow[] => {
    return (
      metrics?.by_output_type.map((item): ChartRow => {
        const base = {
          name: item.output_type,
          percentage: item.percentage,
          fill: CHART_VARS[item.output_type] || CHART_VARS.Other,
        };

        if (item.output_type === 'Other' && otherBreakdown.length > 0) {
          const segments: OtherSegment[] = otherBreakdown.map((entry, i) => ({
            key: `other_${i}`,
            label: formatMimeLabel(entry.mime_type),
            mime: entry.mime_type,
            count: entry.workflow_count,
            fill: getChartPaletteColor(7 + i),
          }));

          return {
            ...base,
            count: segments.reduce((sum, seg) => sum + seg.count, 0),
            _segments: segments,
          };
        }

        return {
          ...base,
          count: item.workflow_count,
        };
      }) ?? []
    );
  }, [metrics, otherBreakdown]);

  const chartConfig = useMemo(() => {
    const config: ChartConfig = { count: { label: 'Workflows' } };
    chartData.forEach((item) => {
      config[item.name] = {
        label: item.name,
        color: item.fill,
      };
      item._segments?.forEach((segment) => {
        config[segment.key] = {
          label: segment.label,
          color: segment.fill,
        };
      });
    });
    return config;
  }, [chartData]);

  const onSegmentHover = useCallback(
    (segment: OtherSegment | null, event?: MouseEvent<SVGElement>) => {
      if (segment && event) {
        setHoveredSegment({
          segment,
          x: event.clientX,
          y: event.clientY,
        });
        return;
      }
      setHoveredSegment(null);
    },
    [],
  );

  const barShape = useMemo(
    () => createOutputBarShape(onSegmentHover),
    [onSegmentHover],
  );

  const chartHeight = Math.max(250, chartData.length * (BAR_SIZE + 16));

  if (isLoading) {
    return (
      <div className='flex h-full flex-col gap-4'>
        <Skeleton className='h-8 w-64' />
        <Card className='flex min-h-0 flex-1 flex-col'>
          <CardContent className='flex-1 pt-6'>
            <Skeleton className='h-full min-h-[250px] w-full' />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !metrics || metrics.by_output_type.length === 0) {
    return (
      <div className='space-y-4'>
        <SectionHeading>Output & Deliverables</SectionHeading>
        <div className='flex h-[120px] items-center justify-center text-muted-foreground'>
          <p className='text-sm'>
            {error?.message || 'No output data available'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='flex h-full flex-col gap-4'>
      <SectionHeading>Output & Deliverables</SectionHeading>
      <Card className='flex min-h-0 flex-1 flex-col'>
        <CardHeader className='pb-2'>
          <CardTitle className='text-base font-semibold'>
            Workflows by Output Type
          </CardTitle>
        </CardHeader>
        <CardContent className='relative flex-1'>
          <ChartContainer
            config={chartConfig}
            className='aspect-auto! w-full'
            style={{ height: chartHeight }}
          >
            <BarChart
              data={chartData}
              layout='vertical'
              barSize={BAR_SIZE}
              margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
            >
              <CartesianGrid horizontal={false} strokeDasharray='3 3' />
              <XAxis type='number' tickLine={false} axisLine={false} />
              <YAxis
                type='category'
                dataKey='name'
                width={110}
                tickLine={false}
                axisLine={false}
              />
              <ChartTooltip content={<OutputTypeTooltip />} />
              <Bar
                dataKey='count'
                shape={barShape as never}
                radius={[0, 4, 4, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ChartContainer>

          {hoveredSegment && (
            <div
              className='pointer-events-none fixed z-50 rounded-lg border bg-background px-3 py-2 text-xs shadow-md'
              style={{
                left: hoveredSegment.x + 12,
                top: hoveredSegment.y + 12,
              }}
            >
              <p className='font-medium'>{hoveredSegment.segment.mime}</p>
              <p className='text-muted-foreground'>
                {hoveredSegment.segment.count} workflow
                {hoveredSegment.segment.count === 1 ? '' : 's'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
